import { toRaw } from 'vue'
import { storeToRefs } from 'pinia'
import { useImageReviewStore } from 'Store/pages/imageReview/imageReviewStore'
import {
  MARKER_STROKE_COLOR,
  MARKER_HOVER_STROKE_COLOR,
  MARKER_STROKE_WEIGHT,
  MARKER_MEASUREMENT_HOVERED_STROKE_WEIGHT,
  MARKER_MEASUREMENT_STROKE_COLOR,
} from './constants'
import { useMarkerStore } from '@/store/pages/imageReview/markerStore'

function handleMeasurementCreate(markerEvent) {
  const reviewStore = useImageReviewStore()
  const { addTempMeasurement } = reviewStore.measurementsStore
  const { currentAnnotation } = storeToRefs(reviewStore.annotationsStore)
  const marker = markerEvent.marker
  const points = marker.state.anchorPoints.map((anchorPoint) => [anchorPoint.x, anchorPoint.y])
  const currentAnnotationUuid = currentAnnotation.value?.annotationUuid
  const tempId = addTempMeasurement(points, currentAnnotationUuid ? currentAnnotationUuid : null)

  marker.controller.selectMarker()
  marker.state.isSelectionLocked = true
  marker.state.meta = {
    ...marker.state.meta,
    id: tempId,
  }
}

/**
 * Callback funciton that is triaged by
 * markerSelectedCallback when the selected marker
 * corresponds to a measurement annotation.
 * @param markerEvent
 */
function handleMeasurementSelected(markerEvent) {
  const reviewStore = useImageReviewStore()
  const markerStore = useMarkerStore()
  const { currentMeasurement, measurements } = storeToRefs(reviewStore.measurementsStore)
  const { setCurrentMeasurement } = reviewStore.measurementsStore
  const { stageAnnotationByUuid } = reviewStore.annotationsStore
  const marker = markerEvent.marker
  const markerArea = markerEvent.markerArea
  const state = marker.state
  const measurementUuid = state.meta?.id
  const points = state.anchorPoints.map((anchorPoint) => [anchorPoint.x, anchorPoint.y])

  if (markerStore.selectedMarkers.length > 0) {
    marker.controller.deselectMarker()
    return
  }

  if (currentMeasurement.value) {
    const existingCurrentMeasurementMarker = markerArea.selectedMarkers.find(
      (marker) => marker.state.meta.id === currentMeasurement.value.measurementUuid
    )
    if (existingCurrentMeasurementMarker) existingCurrentMeasurementMarker.controller.deselectMarker()
  }

  if (measurementUuid) {
    const matchingMeasurement = measurements.value?.find(
      (measurement) => measurement.measurementUuid === measurementUuid
    )
    setCurrentMeasurement({ ...toRaw(matchingMeasurement), points: points })
    if (matchingMeasurement.annotationUuid) stageAnnotationByUuid(matchingMeasurement.annotationUuid)
  }
}

/**
 * Callback funciton that is triaged by
 * markerDeselecrtedCallback when the deselected marker
 * corresponds to a measurement
 * @param markerEvent
 */
function handleMeasurementDeselected(markerEvent) {
  const reviewStore = useImageReviewStore()
  const { currentMeasurement } = storeToRefs(reviewStore.measurementsStore)
  const { setCurrentMeasurement } = reviewStore.measurementsStore

  const marker = markerEvent.marker
  const uuid = marker.state.meta.id
  marker.state.strokeColor = MARKER_MEASUREMENT_STROKE_COLOR
  marker.state.strokeWeight = MARKER_STROKE_WEIGHT
  if (uuid === currentMeasurement.value?.measurementUuid) {
    setCurrentMeasurement()
  }
}

function handleMeasurementMouseEntered(markerEvent) {
  const reviewStore = useImageReviewStore()
  const { measurements, currentMeasurement } = storeToRefs(reviewStore.measurementsStore)
  const { setCurrentHoveredMeasurement } = reviewStore.measurementsStore
  const { hoverAnnotationByUuid } = reviewStore.annotationsStore
  const marker = markerEvent.marker
  const state = marker.state
  const measurementUuid = state.meta.id
  const matchingMeasurement = measurements.value?.find((measurement) => measurement.measurementUuid === measurementUuid)
  const points = state.anchorPoints.map((anchorPoint) => [anchorPoint.x, anchorPoint.y])

  if (!marker.state.isSelected) {
    marker.state.strokeColor = MARKER_HOVER_STROKE_COLOR
    marker.state.strokeWeight = MARKER_MEASUREMENT_HOVERED_STROKE_WEIGHT
    if (!currentMeasurement.value) {
      setCurrentHoveredMeasurement({ ...toRaw(matchingMeasurement), points: points })
      if (matchingMeasurement?.annotationUuid) hoverAnnotationByUuid(matchingMeasurement.annotationUuid)
    }
  }
}

function handleMeasurementMouseLeave(markerEvent) {
  const reviewStore = useImageReviewStore()
  const { setCurrentHoveredMeasurement } = reviewStore.measurementsStore
  const { unhoverAnnotationByUuid } = reviewStore.annotationsStore
  const marker = markerEvent.marker
  const annotationUuid = marker.state.meta?.annotationUuid
  if (!marker.state.isSelected) {
    marker.state.strokeColor = MARKER_STROKE_COLOR
    marker.state.strokeWeight = MARKER_STROKE_WEIGHT
  }
  setCurrentHoveredMeasurement(null)
  if (annotationUuid) {
    unhoverAnnotationByUuid(annotationUuid)
  }
}

function handleMeasurementEdit(markerEvent) {
  const reviewStore = useImageReviewStore()
  const { editMeasurementPointAndSetToCurrent } = reviewStore.measurementsStore

  const marker = markerEvent.marker
  const state = marker.state
  const uuid = marker.state.meta.id
  const points = state.anchorPoints.map((anchorPoint) => [anchorPoint.x, anchorPoint.y])

  editMeasurementPointAndSetToCurrent(uuid, points)
}

export {
  handleMeasurementCreate,
  handleMeasurementSelected,
  handleMeasurementDeselected,
  handleMeasurementMouseEntered,
  handleMeasurementMouseLeave,
  handleMeasurementEdit,
}
